import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from './components/ProtectedRoute';
import ToastProvider from './components/ToastProvider';
import LoginPage from './pages/login/index';
import Dashboard from './pages/dashboard/index';
import ProgressReports from './pages/progress-reports/index';
import ActivityTracking from './pages/activity-tracking/index';
import NutritionTracking from './pages/nutrition-tracking/index';
import ProfileSettings from './pages/profile-settings/index';
import SleepAnalysis from './pages/sleep-analysis/index';
import NotificationsCenter from './pages/notifications-center/index';
import SocialHub from './pages/social-hub/index';
import DuelHistory from './pages/duel-history/index';

import PrivacyPolicy from './pages/privacy-policy/index';
import NotFound from './pages/NotFound';
import StravaCallback from './pages/strava-callback/index';

// Retry-capable lazy loader to handle transient chunk fetch failures
const lazyWithRetry = (importFn, retries = 3, delay = 500) =>
  lazy(() => {
    const attempt = (n) =>
      importFn()?.catch((err) => {
        if (n <= 0) throw err;
        return new Promise((resolve) => setTimeout(resolve, delay))?.then(() => attempt(n - 1));
      });
    return attempt(retries);
  });

// Lazy-loaded pages for code splitting
const LoginPageLazy = lazyWithRetry(() => import('./pages/login'));
const DashboardLazy = lazyWithRetry(() => import('./pages/dashboard'));
const ProgressReportsLazy = lazyWithRetry(() => import('./pages/progress-reports'));
const ActivityTrackingLazy = lazyWithRetry(() => import('./pages/activity-tracking'));
const NutritionTrackingLazy = lazyWithRetry(() => import('./pages/nutrition-tracking'));
const ProfileSettingsLazy = lazyWithRetry(() => import('./pages/profile-settings'));
const Register = lazyWithRetry(() => import('./pages/register'));
const SleepAnalysisLazy = lazyWithRetry(() => import('./pages/sleep-analysis'));
const NotificationsCenterLazy = lazyWithRetry(() => import('./pages/notifications-center'));
const SocialHubLazy = lazyWithRetry(() => import('./pages/social-hub'));
const DuelHistoryLazy = lazyWithRetry(() => import('./pages/duel-history'));
const PasswordReset = lazyWithRetry(() => import('./pages/password-reset'));

// Page loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="text-center">
      <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
      <p className="mt-4 text-muted-foreground">Loading...</p>
    </div>
  </div>
);

// Inner component that has access to location for AnimatePresence key
const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <RouterRoutes location={location} key={location?.pathname}>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<Register />} />
        <Route path="/password-reset" element={<PasswordReset />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/strava-callback" element={<StravaCallback />} />
        
        {/* Protected routes */}
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/progress-reports" element={<ProtectedRoute><ProgressReports /></ProtectedRoute>} />
        <Route path="/activity-tracking" element={<ProtectedRoute><ActivityTracking /></ProtectedRoute>} />
        <Route path="/nutrition-tracking" element={<ProtectedRoute><NutritionTracking /></ProtectedRoute>} />
        <Route path="/profile-settings" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
        <Route path="/sleep-analysis" element={<ProtectedRoute><SleepAnalysis /></ProtectedRoute>} />
        <Route path="/notifications-center" element={<ProtectedRoute><NotificationsCenter /></ProtectedRoute>} />
        <Route path="/social-hub" element={<ProtectedRoute><SocialHub /></ProtectedRoute>} />
        <Route path="/duel-history" element={<ProtectedRoute><DuelHistory /></ProtectedRoute>} />
        
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
    </AnimatePresence>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <ToastProvider>
          <Suspense fallback={<PageLoader />}>
            <AnimatedRoutes />
          </Suspense>
        </ToastProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
